import React, { useEffect, useState } from "react";
import { BASE_URL } from "../api";

const API = `${BASE_URL}/api`;

/* ─────────────────────────────────────────────────────────────
   DESIGN TOKENS
───────────────────────────────────────────────────────────── */
const T = {
  navy: "#0D1F3C",
  blue: "#1A4F8A",
  sky: "#3B82F6",
  green: "#16A34A",
  red: "#DC2626",
  purple: "#7C3AED",
  amber: "#D97706",
  slate: "#475569",
  steel: "#64748B",
  mist: "#F1F5F9",
  border: "#E2E8F0",
  white: "#FFFFFF",
  shadow: "0 4px 24px rgba(13,31,60,0.10)",
  radius: "12px",
};

/* ─────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────── */

const getAuthHeader = (token) => {
  if (!token) return {};
  if (token.split(".").length === 3) {
    return { Authorization: `Bearer ${token}` };
  }
  return { Authorization: `Token ${token}` };
};

const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  const headers = {
    ...getAuthHeader(token),
    ...(options.body && !(options.body instanceof FormData)
      ? { "Content-Type": "application/json" }
      : {}),
  };
  const res = await fetch(url, { ...options, headers });
  if (!res.ok) throw new Error("Request failed");
  return res.json();
};

/* ─────────────────────────────────────────────────────────────
   GLOBAL STYLE
───────────────────────────────────────────────────────────── */

const GlobalStyle = () => {
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

      * { box-sizing: border-box; margin: 0; padding: 0; }

      body {
        background: linear-gradient(135deg, #EFF6FF 0%, #F8FAFC 60%, #F0FDF4 100%);
        font-family: 'Inter', sans-serif;
        min-height: 100vh;
      }

      /* ── TOPBAR ── */
      .topbar {
        background: ${T.navy};
        padding: 0 32px;
        height: 64px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        box-shadow: 0 2px 16px rgba(13,31,60,0.18);
        position: sticky;
        top: 0;
        z-index: 100;
      }

      .topbar-brand {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .topbar-logo {
        width: 36px;
        height: 36px;
        background: linear-gradient(135deg, ${T.sky}, ${T.blue});
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        font-weight: 800;
        color: white;
      }

      .topbar-title {
        font-size: 18px;
        font-weight: 700;
        color: white;
        letter-spacing: -0.3px;
      }

      .topbar-right {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .topbar-user {
        display: flex;
        align-items: center;
        gap: 8px;
        color: rgba(255,255,255,0.75);
        font-size: 14px;
        font-weight: 500;
      }

      .user-avatar {
        width: 34px;
        height: 34px;
        background: linear-gradient(135deg, ${T.sky}, ${T.purple});
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 700;
        color: white;
      }

      .logout-btn {
        height: 36px;
        border: 1.5px solid rgba(255,255,255,0.25);
        background: rgba(255,255,255,0.08);
        color: white;
        padding: 0 16px;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 600;
        font-size: 13px;
        display: flex;
        align-items: center;
        gap: 6px;
        transition: all 0.2s;
        font-family: 'Inter', sans-serif;
      }

      .logout-btn:hover {
        background: rgba(220,38,38,0.85);
        border-color: transparent;
      }

      /* ── ROOT ── */
      .root {
        padding: 28px 32px;
        max-width: 1400px;
        margin: 0 auto;
      }

      /* ── PAGE CARD ── */
      .page-card {
        background: white;
        border-radius: 20px;
        border: 1px solid ${T.border};
        box-shadow: 0 2px 24px rgba(13,31,60,0.07);
        overflow: hidden;
      }

      .page-header {
        padding: 22px 28px;
        border-bottom: 1px solid ${T.border};
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: linear-gradient(to right, #FAFBFF, white);
      }

      .page-title {
        font-size: 22px;
        font-weight: 800;
        color: ${T.navy};
        letter-spacing: -0.4px;
      }

      .page-subtitle {
        margin-top: 4px;
        color: ${T.steel};
        font-size: 13px;
        font-weight: 500;
      }

      .page-body { padding: 28px; }

      /* ── SEARCH ── */
      .search-wrap {
        position: relative;
        display: inline-block;
      }

      .search-icon {
        position: absolute;
        left: 13px;
        top: 50%;
        transform: translateY(-50%);
        color: ${T.steel};
        font-size: 15px;
        pointer-events: none;
      }

      .search-input {
        height: 42px;
        border: 1.5px solid ${T.border};
        border-radius: 10px;
        padding: 0 14px 0 38px;
        min-width: 280px;
        outline: none;
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        color: ${T.navy};
        transition: border-color 0.2s;
        background: ${T.mist};
      }

      .search-input:focus {
        border-color: ${T.sky};
        background: white;
      }

      /* ── CAMP GRID ── */
      .camp-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 18px;
      }

      .camp-card {
        border: 1.5px solid ${T.border};
        border-radius: 16px;
        padding: 22px;
        background: white;
        cursor: pointer;
        transition: all 0.22s;
        position: relative;
        overflow: hidden;
      }

      .camp-card::before {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0;
        height: 4px;
        background: linear-gradient(90deg, ${T.sky}, ${T.purple});
        opacity: 0;
        transition: opacity 0.2s;
      }

      .camp-card:hover {
        transform: translateY(-4px);
        border-color: ${T.sky};
        box-shadow: 0 8px 28px rgba(59,130,246,0.13);
      }

      .camp-card:hover::before { opacity: 1; }

      .camp-name {
        font-size: 17px;
        font-weight: 700;
        color: ${T.navy};
        margin-bottom: 12px;
        line-height: 1.3;
      }

      .camp-sub {
        color: ${T.steel};
        font-size: 13.5px;
        margin-bottom: 7px;
        display: flex;
        align-items: center;
        gap: 7px;
      }

      .camp-footer {
        margin-top: 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .badge {
        display: inline-flex;
        padding: 5px 12px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.3px;
        text-transform: uppercase;
      }

      .badge-active {
        background: linear-gradient(135deg, #DCFCE7, #BBF7D0);
        color: #166534;
      }

      .camp-arrow {
        color: ${T.sky};
        font-size: 18px;
        font-weight: 700;
      }

      /* ── SUMMARY GRID ── */
      .summary-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 16px;
        margin-bottom: 28px;
      }

      .summary-card {
        border-radius: 16px;
        padding: 20px 22px;
        position: relative;
        overflow: hidden;
      }

      .summary-card-green { background: linear-gradient(135deg, #F0FDF4, #DCFCE7); border: 1.5px solid #BBF7D0; }
      .summary-card-blue  { background: linear-gradient(135deg, #EFF6FF, #DBEAFE); border: 1.5px solid #BFDBFE; }
      .summary-card-red   { background: linear-gradient(135deg, #FFF5F5, #FEE2E2); border: 1.5px solid #FECACA; }
      .summary-card-purple{ background: linear-gradient(135deg, #F5F3FF, #EDE9FE); border: 1.5px solid #DDD6FE; }

      .summary-icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        margin-bottom: 14px;
      }

      .summary-label {
        font-size: 11px;
        color: ${T.slate};
        margin-bottom: 6px;
        text-transform: uppercase;
        font-weight: 700;
        letter-spacing: 0.5px;
      }

      .summary-value {
        font-size: 32px;
        font-weight: 800;
        letter-spacing: -1px;
        line-height: 1;
      }

      /* ── TABLE ── */
      .table-scroll { overflow-x: auto; border-radius: 12px; border: 1px solid ${T.border}; }

      .table {
        width: 100%;
        border-collapse: collapse;
        min-width: 1100px;
      }

      .table thead tr {
        background: linear-gradient(to right, #F8FAFF, ${T.mist});
      }

      .table th {
        color: ${T.slate};
        padding: 13px 16px;
        text-align: left;
        font-size: 11px;
        text-transform: uppercase;
        font-weight: 700;
        letter-spacing: 0.5px;
        border-bottom: 1.5px solid ${T.border};
      }

      .table td {
        padding: 14px 16px;
        border-bottom: 1px solid #F1F5F9;
        font-size: 14px;
        color: ${T.navy};
        vertical-align: middle;
      }

      .table tbody tr:hover { background: #FAFBFF; }
      .table tbody tr:last-child td { border-bottom: none; }

      .patient-name {
        font-weight: 700;
        color: ${T.navy};
      }

      .count-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 700;
      }

      .count-green { background: #DCFCE7; color: #166534; }
      .count-red   { background: #FEE2E2; color: #991B1B; }

      /* ── PILLS ── */
      .pill {
        display: inline-flex;
        align-items: center;
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 11.5px;
        font-weight: 600;
        margin: 2px;
      }

      .pill-green { background: #DCFCE7; color: #166534; }
      .pill-red   { background: #FEE2E2; color: #991B1B; }

      .status-pill {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 5px 12px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.3px;
      }

      .status-done    { background: #DCFCE7; color: #166534; }
      .status-pending { background: #FEF3C7; color: #92400E; }

      .medibuddy-btn {
        height: 40px;
        border: none;
        background: linear-gradient(135deg, #16A34A, #15803D);
        color: white;
        padding: 0 18px;
        border-radius: 10px;
        cursor: pointer;
        font-weight: 600;
        font-size: 13.5px;
        display: flex;
        align-items: center;
        gap: 7px;
        transition: all 0.2s;
        font-family: 'Inter', sans-serif;
      }

      .medibuddy-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(22,163,74,0.25);
      }

      /* ── BUTTONS ── */
      .back-btn {
        height: 40px;
        border: none;
        background: ${T.navy};
        color: white;
        padding: 0 18px;
        border-radius: 10px;
        cursor: pointer;
        font-weight: 600;
        font-size: 13.5px;
        display: flex;
        align-items: center;
        gap: 7px;
        transition: background 0.2s;
        font-family: 'Inter', sans-serif;
      }

      .back-btn:hover { background: ${T.blue}; }

      .empty {
        padding: 60px 40px;
        text-align: center;
        color: ${T.steel};
        font-size: 15px;
      }

      .empty-icon { font-size: 40px; margin-bottom: 12px; }

      /* ── RESPONSIVE ── */
      @media (max-width: 900px) {
        .summary-grid { grid-template-columns: 1fr 1fr; }
        .root { padding: 16px; }
      }

      @media (max-width: 600px) {
        .summary-grid { grid-template-columns: 1fr; }
        .topbar { padding: 0 16px; }
        .topbar-title { font-size: 15px; }
        .page-header { flex-direction: column; gap: 14px; align-items: stretch; }
        .search-input { width: 100%; min-width: unset; }
        .search-wrap { width: 100%; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  return null;
};

/* ─────────────────────────────────────────────────────────────
   TOP BAR
───────────────────────────────────────────────────────────── */

const TopBar = ({ onLogout }) => {
  const username =
    localStorage.getItem("username") || "Admin";

  return (
    <div className="topbar">
      <div className="topbar-brand">
        <div className="topbar-logo">C</div>
        <span className="topbar-title">CampManager</span>
      </div>

      <div className="topbar-right">
        {/* <div className="topbar-user">
          <div className="user-avatar">
            {username.charAt(0).toUpperCase()}
          </div>
          <span>{username}</span>
        </div> */}

        <button className="logout-btn" onClick={onLogout}>
          ⏻ Logout
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   SUMMARY CARD
───────────────────────────────────────────────────────────── */

const SummaryCard = ({ label, value, color, bgClass, icon }) => (
  <div className={`summary-card ${bgClass}`}>
    <div className="summary-icon">{icon}</div>
    <div className="summary-label">{label}</div>
    <div className="summary-value" style={{ color }}>{value}</div>
  </div>
);

/* ─────────────────────────────────────────────────────────────
   ACTUAL CASES VIEW
───────────────────────────────────────────────────────────── */

const ActualCasesView = ({ camp, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [summary, setSummary] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const [summaryRes, patientsRes] = await Promise.all([
        apiRequest(`${API}/preview-bill/${camp.id}/`),
        apiRequest(`${API}/billing-preview-patients/${camp.id}/`),
      ]);
      setSummary(summaryRes?.summary || {});
      const patientData =
        Array.isArray(patientsRes) ? patientsRes
        : Array.isArray(patientsRes?.patients) ? patientsRes.patients
        : Array.isArray(patientsRes?.results) ? patientsRes.results
        : [];
      setPatients(patientData);
    } catch (e) {
      console.error(e);
      setSummary({});
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = patients.filter((p) => {
    const q = search.toLowerCase();
    return (
      (p.patient_name || "").toLowerCase().includes(q) ||
      (p.completed_services || []).join(" ").toLowerCase().includes(q) ||
      (p.pending_services || []).join(" ").toLowerCase().includes(q)
    );
  });

  const completedCases = filtered.filter((p) => p.pending_count === 0).length;
  const pendingCases = filtered.filter((p) => p.pending_count > 0).length;

  return (
    <div className="root">
      <div className="page-card">
        <div className="page-header">
          <div>
            <div className="page-title">📋 Actual Cases</div>
            <div className="page-subtitle">
              🏥 {camp.branch_name} &nbsp;·&nbsp; 📍 {camp.location}
            </div>
          </div>
          <button className="back-btn" onClick={onBack}>
            ← Back To Camps
          </button>
        </div>

        <div className="page-body">
          {/* SEARCH */}
          <div style={{ marginBottom: 24 }}>
            <div className="search-wrap">
              <span className="search-icon">🔍</span>
              <input
                className="search-input"
                placeholder="Search patient or service..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="empty">
              <div className="empty-icon">⏳</div>
              Loading patient data...
            </div>
          ) : (
            <>
              {/* SUMMARY */}
              <div className="summary-grid">
                <SummaryCard
                  label="Total Actual Cases"
                  value={summary.actual_cases || patients.length}
                  color={T.green}
                  bgClass="summary-card-green"
                  icon="👥"
                />
                <SummaryCard
                  label="Completed Cases"
                  value={completedCases}
                  color={T.sky}
                  bgClass="summary-card-blue"
                  icon="✅"
                />
                <SummaryCard
                  label="Pending Cases"
                  value={pendingCases}
                  color={T.red}
                  bgClass="summary-card-red"
                  icon="⏰"
                />
                <SummaryCard
                  label="Completion %"
                  value={
                    patients.length
                      ? `${Math.round((completedCases / patients.length) * 100)}%`
                      : "0%"
                  }
                  color={T.purple}
                  bgClass="summary-card-purple"
                  icon="📊"
                />
              </div>

              {/* TABLE */}
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Patient Name</th>
                      <th>Test Date</th>
                      <th>Completed</th>
                      <th>Pending</th>
                      <th>Completed Services</th>
                      <th>Pending Services</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={8}>
                          <div className="empty">
                            <div className="empty-icon">🔍</div>
                            No patients found
                          </div>
                        </td>
                      </tr>
                    )}
                    {filtered.map((p, index) => {
                      const done = p.pending_count === 0;
                      return (
                        <tr key={p.id || index}>
                          <td style={{ color: T.steel, fontWeight: 600 }}>
                            {index + 1}
                          </td>
                          <td className="patient-name">
                            {p.patient_name || "—"}
                          </td>
                          <td style={{ color: T.slate }}>
                            {p.test_date
                              ? new Date(p.test_date).toLocaleDateString("en-IN")
                              : "—"}
                          </td>
                          <td>
                            <span className="count-badge count-green">
                              {p.completed_count || 0}
                            </span>
                          </td>
                          <td>
                            <span className="count-badge count-red">
                              {p.pending_count || 0}
                            </span>
                          </td>
                          <td>
                            {(p.completed_services || []).length > 0 ? (
                              <div style={{ display: "flex", flexWrap: "wrap" }}>
                                {p.completed_services.map((s, i) => (
                                  <span key={i} className="pill pill-green">{s}</span>
                                ))}
                              </div>
                            ) : <span style={{ color: T.steel }}>—</span>}
                          </td>
                          <td>
                            {(p.pending_services || []).length > 0 ? (
                              <div style={{ display: "flex", flexWrap: "wrap" }}>
                                {p.pending_services.map((s, i) => (
                                  <span key={i} className="pill pill-red">{s}</span>
                                ))}
                              </div>
                            ) : <span style={{ color: T.steel }}>—</span>}
                          </td>
                          <td>
                            <span className={`status-pill ${done ? "status-done" : "status-pending"}`}>
                              {done ? "✅ Done" : "⏳ Pending"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   CAMP LIST PAGE
───────────────────────────────────────────────────────────── */

const CampsList = ({ onOpenCamp, onOpenMedibuddy }) => {
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [camps, setCamps] = useState([]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await apiRequest(`${API}/billing-camps/`);
      setCamps(res.results || res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = camps.filter((c) => {
    const q = search.toLowerCase();
    return (
      (c.branch_name || "").toLowerCase().includes(q) ||
      (c.location || "").toLowerCase().includes(q) ||
      (c.client_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="root">
      <div className="page-card">
        <div className="page-header">
          <div>
            <div className="page-title">🏕️ Camps Actual Cases</div>
            <div className="page-subtitle">
              {camps.length} camp{camps.length !== 1 ? "s" : ""} available
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>

            <div className="search-wrap">
              <span className="search-icon">🔍</span>

              <input
                className="search-input"
                placeholder="Search camps..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <button
              className="medibuddy-btn"
              onClick={onOpenMedibuddy}
            >
              💳 Medibuddy
            </button>

          </div>
        </div>

        <div className="page-body">
          {loading ? (
            <div className="empty">
              <div className="empty-icon">⏳</div>
              Loading camps...
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">🔍</div>
              No camps found
            </div>
          ) : (
            <div className="camp-grid">
              {filtered.map((camp) => (
                <div
                  key={camp.id}
                  className="camp-card"
                  onClick={() => onOpenCamp(camp)}
                >
                  <div className="camp-name">{camp.branch_name}</div>
                  <div className="camp-sub">📍 {camp.location}</div>
                  <div className="camp-sub">🏢 {camp.client_name}</div>
                  <div className="camp-sub">
                    📅 {camp.start_date} to {camp.end_date}
                  </div>
                  <div className="camp-footer">
                    <span className="badge badge-active">
                      {camp.billing_mode}
                    </span>
                    <span className="camp-arrow">→</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ClientMedibuddyBillScreen = ({ onBack }) => {

  const CLIENT_ID = 57;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fmt = (v) =>
    Number(v || 0).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    });

  const getClientMedibuddyBill = async (start, end) => {
    return apiRequest(
      `${API}/client-medibuddy-bill/${CLIENT_ID}/?start_date=${start}&end_date=${end}`
    );
  };

  const load = async () => {
    try {

      if (!startDate || !endDate) {
        alert("Please select date range");
        return;
      }

      if (startDate > endDate) {
        alert("Start date cannot be after end date");
        return;
      }

      setLoading(true);

      const res = await getClientMedibuddyBill(
        startDate,
        endDate
      );

      setData(res);

    } catch (e) {
      console.error(e);
      alert("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="root">
      <div className="page-card">

        <div className="page-header">
          <div>
            <div className="page-title">
              💳 Medibuddy Client Billing
            </div>

            <div className="page-subtitle">
              Fetch billing by date range
            </div>
          </div>

          <button className="back-btn" onClick={onBack}>
            ← Back
          </button>
        </div>

        <div className="page-body">

          {/* FILTERS */}
          <div
            style={{
              display: "flex",
              gap: 12,
              marginBottom: 24,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >

            <input
              type="date"
              className="search-input"
              value={startDate}
              onChange={(e) =>
                setStartDate(e.target.value)
              }
            />

            <input
              type="date"
              className="search-input"
              value={endDate}
              onChange={(e) =>
                setEndDate(e.target.value)
              }
            />

            <button
              className="medibuddy-btn"
              onClick={load}
              disabled={loading}
            >
              {loading ? "Loading..." : "🔍 Fetch Bill"}
            </button>

          </div>

          {/* STATES */}
          {loading && (
            <div className="empty">
              <div className="empty-icon">⏳</div>
              Loading billing data...
            </div>
          )}

          {!loading && !data && (
            <div className="empty">
              <div className="empty-icon">📅</div>
              Select a date range to fetch billing
            </div>
          )}

          {/* DATA */}
          {!loading && data && (
            <div>

              {data.details.map((camp, i) => (

                <div
                  key={i}
                  style={{
                    marginBottom: 28,
                    border: `1px solid ${T.border}`,
                    borderRadius: 14,
                    padding: 20,
                  }}
                >

                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      marginBottom: 14,
                      color: T.navy,
                    }}
                  >
                    🏕️ {camp.camp_name}
                  </div>

                  <div className="table-scroll">
                    <table className="table">

                      <thead>
                        <tr>
                          <th>Component</th>
                          <th>Units</th>
                        </tr>
                      </thead>

                      <tbody>
                        {camp.details.map((row, j) => (
                          <tr key={j}>
                            <td>{row.component}</td>
                            <td>{row.units}</td>
                          </tr>
                        ))}
                      </tbody>

                    </table>
                  </div>

                  <div
                    style={{
                      marginTop: 14,
                      fontSize: 14,
                      color: T.slate,
                    }}
                  >
                    Cases: <b>{camp.summary.actual_cases}</b>
                    {" "} | {" "}
                    Days: <b>{camp.summary.camp_days}</b>
                  </div>

                </div>

              ))}

              {/* FINAL TOTAL */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg,#EFF6FF,#DBEAFE)",
                  border: "1.5px solid #BFDBFE",
                  borderRadius: 16,
                  padding: 24,
                  marginTop: 20,
                }}
              >

                <div
                  style={{
                    fontSize: 14,
                    color: T.slate,
                    marginBottom: 8,
                    fontWeight: 700,
                  }}
                >
                  Client Summary
                </div>

                <div
                  style={{
                    fontSize: 14,
                    color: T.steel,
                  }}
                >
                  Total Cases: <b>{data.summary.total_cases}</b>
                  {" "} | {" "}
                  Total Camp Days: <b>{data.summary.total_days}</b>
                </div>

              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   ROOT
───────────────────────────────────────────────────────────── */

export default function ActualCasesRoot() {

  const [selectedCamp, setSelectedCamp] = useState(null);

  const [showMedibuddy, setShowMedibuddy] =
    useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");

    window.location.href = "/login";
  };

  return (
    <>
      <GlobalStyle />

      <TopBar onLogout={handleLogout} />

      {showMedibuddy ? (

        <ClientMedibuddyBillScreen
          onBack={() => setShowMedibuddy(false)}
        />

      ) : !selectedCamp ? (

        <CampsList
          onOpenCamp={setSelectedCamp}
          onOpenMedibuddy={() =>
            setShowMedibuddy(true)
          }
        />

      ) : (

        <ActualCasesView
          camp={selectedCamp}
          onBack={() => setSelectedCamp(null)}
          onOpenMedibuddy={() =>
            setShowMedibuddy(true)
          }
        />

      )}
    </>
  );
}